import { PagedResponse, QueryParams } from "@ofqual/portal-core/core/types";
import { AnyAction } from "redux";
import { FoiItem } from "../domain/FoiItem";
import { Attachment } from "../domain/Attachment";
import { MODULE_NAME } from "../moduleName";

import { FoiEvent, FoiEventComplete } from "../domain/FoiEvent";
import { ShareRecord } from "../domain/ShareRecord";

export interface ActionPayload<T> extends AnyAction {
  type: string;
  payload: T;
}

export const LOADED_ITEMS = `${MODULE_NAME}/LOADED_ITEMS`;
export const LOADING_ITEMS = `${MODULE_NAME}/LOADING_ITEMS`;

export const LOADED_ITEM = `${MODULE_NAME}/LOADED_ITEM`;
export const LOADING_ITEM = `${MODULE_NAME}/LOADING_ITEM`;
export const SAVED_ITEM = `${MODULE_NAME}/SAVED_ITEM`;

export const UPDATING_ITEM = `${MODULE_NAME}/UPDATING_ITEM`;
export const UPDATING_ITEM_VALIDATION_FAIL = `${MODULE_NAME}/UPDATING_ITEM_VALIDATION_FAIL`;

export const INPUTTING_ITEM = `${MODULE_NAME}/INPUTTING_ITEM`;
export const CREATING_ITEM = `${MODULE_NAME}/CREATING_ITEM`;
export const CREATING_ITEM_VALIDATION_FAIL = `${MODULE_NAME}/CREATING_ITEM_VALIDATION_FAIL`;
export const CREATED_ITEM = `${MODULE_NAME}/CREATED_ITEM`;

export const ADD_INTERNAL_MESSAGE = `${MODULE_NAME}/ADD_INTERNAL_MESSAGE`;

export const ADD_CASE_NOTE = `${MODULE_NAME}/ADD_CASE_NOTE`;
export const ADD_CASE_NOTE_ERROR = `${MODULE_NAME}/ADD_CASE_NOTE_ERROR`;
export const CLEAR_ERROR = `${MODULE_NAME}/CLEAR_ERROR`;

export const ASSIGN_FOI = `${MODULE_NAME}/ASSIGN_FOI`;
export const UNASSIGN_FOI = `${MODULE_NAME}/UNASSIGN_FOI`;

export const CLOSE_FOI = `${MODULE_NAME}/CLOSE_FOI`;
export const REOPEN_FOI = `${MODULE_NAME}/REOPEN_FOI`;
export const CANCEL_FOI = `${MODULE_NAME}/CANCEL_FOI`;

export const DELETE_ATTACHMENT = `${MODULE_NAME}/DELETE_ATTACHMENT`;

export const ADD_FOI_EVENT = `${MODULE_NAME}/ADD_FOI_EVENT`;
export const COMPLETE_FOI_EVENT = `${MODULE_NAME}/COMPLETE_FOI_EVENT`;
export const ADD_FOI_EVENT_ERROR = `${MODULE_NAME}/ADD_FOI_EVENT_ERROR`;

export const UPDATING_SHARE = `${MODULE_NAME}/UPDATING_SHARE`;
export const LOADED_SHARE = `${MODULE_NAME}/LOADED_SHARE`;

export const UPDATE_STAGE = `${MODULE_NAME}/UPDATE_STAGE`;

export const ADD_PUBLIC_MESSAGE = `${MODULE_NAME}/ADD_PUBLIC_MESSAGE`;

export const addFoiEvent = (foiId: string, newEvent: FoiEvent) => ({
  type: ADD_FOI_EVENT,
  payload: { foiId, newEvent },
});

export const completeFoiEvent = (
  foiId: string,
  foiEventId: string,
  completionEvent: FoiEventComplete
) => ({
  type: COMPLETE_FOI_EVENT,
  payload: { foiId, foiEventId, completionEvent },
});

export const loadedItems = (payload: PagedResponse<FoiItem>) => ({
  type: LOADED_ITEMS,
  payload,
});
export const loadingItems = (payload: QueryParams) => ({
  type: LOADING_ITEMS,
  payload,
});

export const loadedItem = (item: FoiItem, share?: ShareRecord | null) => ({
  type: LOADED_ITEM,
  payload: { item, share },
});

export const savedItem = (item: FoiItem) => ({
  type: SAVED_ITEM,
  payload: item,
});

export const loadingItem = (id: string) => ({
  type: LOADING_ITEM,
  payload: id,
});

export const updatingItem = (
  id: string,
  patchDocument: object,
  documentsToDelete: Attachment[],
  newFiles: File[]
) => ({
  type: UPDATING_ITEM,
  payload: { id, patchDocument, documentsToDelete, newFiles },
});

export const createItem = (item: FoiItem) => ({
  type: CREATING_ITEM,
  payload: item,
});

export const inputtingItem = () => ({
  type: INPUTTING_ITEM,
});

export const addInternalMessage = (
  id: string,
  files: File[],
  subject: string,
  message: string,
  recipients: string[]
) => ({
  type: ADD_INTERNAL_MESSAGE,
  payload: { id, files, subject, message, recipients },
});

export const addPublicMessage = (
  id: string,
  files: File[],
  subject: string,
  message: string,
  responseTypeId: number,
  formalResponseTypeId?: number
) => ({
  type: ADD_PUBLIC_MESSAGE,
  payload: { id, files, subject, message, responseTypeId, formalResponseTypeId },
});

export const addCaseNote = (
  id: string,
  caseFiles: File[],
  caseNote: string,
  subject: string
) => ({
  type: ADD_CASE_NOTE,
  payload: { id, caseFiles, caseNote, subject },
});

export const addCaseNoteError = (message: string) => ({
  type: ADD_CASE_NOTE_ERROR,
  payload: { message },
});

export const addFoiEventError = (message: string) => ({
  type: ADD_FOI_EVENT_ERROR,
  payload: { message },
});

export const assignCase = (id: string, upn: string, displayName: string) => ({
  type: ASSIGN_FOI,
  payload: { id, upn, displayName },
});

export const unassignCase = (id: string) => ({
  type: UNASSIGN_FOI,
  payload: { id },
});

export const closeFoi = (id: string) => ({
  type: CLOSE_FOI,
  payload: { id },
});

export const cancelFoi = (id: string) => ({
  type: CANCEL_FOI,
  payload: { id },
});

export const reopenFoi = (id: string) => ({
  type: REOPEN_FOI,
  payload: { id },
});

export const deleteSelectedAttachment = (attachment: Attachment) => ({
  type: DELETE_ATTACHMENT,
  payload: {
    attachment,
  },
});

export const updatingShare = (id: string, share: ShareRecord) => ({
  type: UPDATING_SHARE,
  payload: { id, share },
});

export const loadedShare = (share: ShareRecord | null) => ({
  type: LOADED_SHARE,
  payload: share,
});

export const updateStage = (id: string, foiStage: string) => ({
  type: UPDATE_STAGE,
  payload: { id, foiStage },
});
